import {
    Box,
    FormControl,
    Grid,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import React, { ReactElement, useLayoutEffect } from 'react';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import ZoomIn from '@mui/icons-material/ZoomIn';
import ZoomOut from '@mui/icons-material/ZoomOut';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { PDFDocument } from 'pdf-lib';
import FileSaver from 'file-saver';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

const Footer: React.FC<UploadReworkDocumentViewerFooter> = React.forwardRef(
    (props, ref): ReactElement => {
        const {
            onZoomChanged,
            onClose,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            isDocumentSuccess,
            documentPlaceholder,
            documentReason,
            documentMessage,
            documentRef,
            isFullScreen,
            setFullScreen,
        } = props;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [isOpenDropdown, setOpenDropdown] =
            React.useState<boolean>(false);
        const [zoomPx, setZoomPx] = React.useState<number>(20);
        const onHandleZoomOut = React.useCallback(() => {
            if (zoomPx > 0) {
                const newzoomPx = zoomPx - 5;

                setZoomPx(newzoomPx);
            }
        }, [zoomPx]);
        const onHandleZoomIn = React.useCallback(() => {
            if (zoomPx < 100) {
                const newZoomPx = zoomPx + 5;

                setZoomPx(newZoomPx);
            }
        }, [zoomPx]);
        useLayoutEffect(() => {
            if (typeof onZoomChanged === 'function') {
                onZoomChanged(zoomPx);
            }
        }, [zoomPx]);
        const actionFullScreenForLeftViewer = React.useCallback(() => {
            if (typeof setFullScreen === 'function') {
                setFullScreen(!isFullScreen);
            }
        }, [isFullScreen, setFullScreen]);
        const actionDownloadDocumentInView = React.useCallback(async () => {
            if (documentRef && documentRef.current) {
                const { current: documentProxy } = documentRef;
                if (documentProxy?.linkService) {
                    await documentProxy?.linkService.current.pdfDocument
                        ?.getData()
                        .then(async (res) => {
                            const pdfDoc = await PDFDocument.load(res);

                            const base64DataUri = await pdfDoc.saveAsBase64({
                                dataUri: true,
                            });

                            fetch(base64DataUri)
                                .then((resp) => resp.blob())
                                .then((blob) => {
                                    FileSaver.saveAs(blob, documentPlaceholder);
                                });
                        });
                }
            }
        }, [documentPlaceholder, documentRef]);

        React.useImperativeHandle(
            ref,
            () => ({
                /**
                 * check modal rework is already active with no selection dropdown opened
                 */
                isModalFooterActive: () => isOpenDropdown,
                /**
                 * set default document data
                 * @param {RawDocument} _document - document data
                 * @param {RawDocument} _clientId - clientId from packet data
                 * @param {RawDocument} _packetId - packetId from actions in packet data
                 */
            }),
            [isOpenDropdown]
        );

        return (
            <Box className="footer rework-modal-footer">
                <Grid container spacing={2}>
                    <Grid
                        container
                        item
                        xs={12}
                        sm={isFullScreen ? 12 : 8}
                        md={isFullScreen ? 12 : 9}
                        className="task-document-modal"
                    >
                        <Box className="document-metadata">
                            <Grid container direction="row">
                                {isFullScreen === false && (
                                    <Grid
                                        item
                                        xs={8}
                                        direction="column-reverse"
                                    >
                                        {documentMessage && (
                                            <Box className="reasonBox">
                                                <Typography variant="h6">
                                                    Reason
                                                </Typography>
                                                <Typography>
                                                    {documentMessage}
                                                </Typography>
                                            </Box>
                                        )}
                                        {documentReason !== null && (
                                            <Box className="messageBox">
                                                <TextField
                                                    id="outlined-multiline-flexible"
                                                    multiline
                                                    fullWidth
                                                    variant="standard"
                                                    hiddenLabel
                                                    value={documentReason}
                                                    maxRows={4}
                                                    InputProps={{
                                                        disableUnderline: true, // <== added this
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </Grid>
                                )}
                                <Grid item xs={isFullScreen ? 12 : 4}>
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        justifyContent="flex-end"
                                    >
                                        <Box className="zoom-container">
                                            <FormControl
                                                fullWidth
                                                variant="standard"
                                                className="aggregator-zoom"
                                            >
                                                <InputLabel htmlFor="standard-adornment-amount">
                                                    Zoom
                                                </InputLabel>
                                                <Input
                                                    inputProps={{
                                                        'data-testid':
                                                            'test-zoom-label',
                                                        role: 'spinbutton',
                                                        'aria-label': 'zoom',
                                                        'aria-invalid':
                                                            zoomPx < 0 ||
                                                            zoomPx > 100
                                                                ? 'true'
                                                                : 'false',
                                                        'aria-valuenow': zoomPx,
                                                        'aria-valuemax': 100,
                                                        'aria-valuemin': 0,
                                                    }}
                                                    disableUnderline
                                                    value={`${zoomPx}%`}
                                                    disabled
                                                    id="standard-adornment-amount"
                                                    startAdornment={
                                                        <InputAdornment position="start">
                                                            <IconButton
                                                                disabled={
                                                                    !isDocumentSuccess
                                                                }
                                                                title="zoom out"
                                                                onClick={
                                                                    onHandleZoomOut
                                                                }
                                                                //   onMouseDown={handleMouseDownPassword}
                                                                edge="end"
                                                            >
                                                                <ZoomOut />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    }
                                                    endAdornment={
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                disabled={
                                                                    !isDocumentSuccess
                                                                }
                                                                title="zoom in"
                                                                onClick={
                                                                    onHandleZoomIn
                                                                }
                                                                //   onMouseDown={handleMouseDownPassword}
                                                                edge="end"
                                                            >
                                                                <ZoomIn />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    }
                                                />
                                            </FormControl>
                                        </Box>
                                        <IconButton
                                            className="tooltip-button"
                                            aria-label="fullscreen"
                                            disabled={!isDocumentSuccess}
                                            onClick={
                                                actionFullScreenForLeftViewer
                                            }
                                        >
                                            {isFullScreen ? (
                                                <CloseFullscreenIcon />
                                            ) : (
                                                <OpenInFullIcon />
                                            )}
                                        </IconButton>
                                        <IconButton
                                            className="tooltip-button"
                                            aria-label="download"
                                            disabled={!isDocumentSuccess}
                                            onClick={
                                                actionDownloadDocumentInView
                                            }
                                        >
                                            <FileDownloadOutlinedIcon />
                                        </IconButton>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                    {isFullScreen === false && (
                        <Grid
                            item
                            xs={12}
                            sm={4}
                            md={3}
                            className="task-document-modal"
                        >
                            <Box className="saving-upload-rework-document-modal">
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <AppPrimaryButton
                                            title="Close Document"
                                            className="btn-small save-button back-button"
                                            onClick={onClose}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>
        );
    }
);

export default Footer;
